import React, { useEffect, useMemo, useState } from 'react';
import { Table, Tag, Spin, Alert, Button, Space, Typography, Segmented, Drawer } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../api';
import { formatTS } from '../utils/time';

// Coaching Agent Stats > Due Dates: Active and Completed sessions due in the
// chosen range, one row per participant, with Missed Due Date 1/0 (worked out by the
// server: completed after the due date, or not completed and the due date has
// passed -- due today or later is not past due yet). Two views: Summary (per
// agent counts for the month-end count; click Missed Due Date or Sessions for that
// agent's sessions in a side panel) and Sessions (every row).

const PART_LABEL = { pending: 'Pending', completed: 'Completed', canceled: 'Canceled' };
const PART_COLOR = { pending: 'blue', completed: 'green', canceled: 'default' };
const SESSION_LABEL = { active: 'Active', completed: 'Completed' };

const str = (k) => (a, b) => (a[k] || '').localeCompare(b[k] || '');
const num = (k) => (a, b) => (a[k] || 0) - (b[k] || 0);

export default function CoachingDueDatesTab({ from, to, campaignIds, typeIds }) {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState('summary');   // summary | sessions
  const [panel, setPanel]     = useState(null);        // { UserID, AgentName, pastDueOnly } | null

  useEffect(() => {
    if (!from || !to) return;
    setLoading(true);
    api.get('/api/coaching/due-date-report', {
      params: {
        date_from: from,
        date_to:   to,
        ...(campaignIds.length ? { campaign_id: campaignIds.join(',') } : {}),
        ...(typeIds.length ? { request_type_id: typeIds.join(',') } : {}),
      },
    })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [from, to, campaignIds, typeIds]);

  // By agent: sessions, missed due date, on time (completed by the due date),
  // not due yet (not completed, due today or later).
  const byAgent = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const a = (m[r.UserID] ||= { UserID: r.UserID, AgentName: r.AgentName, Sessions: 0, PastDue: 0, OnTime: 0, Open: 0 });
      a.Sessions += 1;
      if (r.PastDue) a.PastDue += 1;
      else if (r.ParticipantStatus === 'completed') a.OnTime += 1;
      else a.Open += 1;
    });
    return Object.values(m).sort((x, y) => y.PastDue - x.PastDue || (x.AgentName || '').localeCompare(y.AgentName || ''));
  }, [rows]);

  const totals = byAgent.reduce((t, a) => ({
    Sessions: t.Sessions + a.Sessions, PastDue: t.PastDue + a.PastDue, OnTime: t.OnTime + a.OnTime, Open: t.Open + a.Open,
  }), { Sessions: 0, PastDue: 0, OnTime: 0, Open: 0 });

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(byAgent.map((a) => ({
      'Agent': a.AgentName, 'Sessions': a.Sessions, 'Missed Due Date': a.PastDue, 'On Time': a.OnTime, 'Not Due Yet': a.Open,
    }))), 'By Agent');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.map((r) => ({
      'Agent':              r.AgentName,
      'Campaign':           r.Campaign ?? '',
      'Request Type':       r.RequestType ?? '',
      'Session ID':         r.SessionID,
      'Session':            r.Title ?? '',
      'Session Status':     SESSION_LABEL[r.SessionStatus] ?? r.SessionStatus,
      'Participant Status': PART_LABEL[r.ParticipantStatus] ?? r.ParticipantStatus,
      'Completed At':       r.CompletedAt ? formatTS(r.CompletedAt) : '',
      'Due Date':           r.DueDate ?? '',
      'Missed Due Date':    r.PastDue,
    }))), 'Sessions');
    XLSX.writeFile(wb, `Coaching Due Dates ${from} to ${to}.xlsx`);
  };

  const sessionColumns = (withAgent) => [
    ...(withAgent ? [{ title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', width: 180, sorter: str('AgentName'), defaultSortOrder: 'ascend' }] : []),
    { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 130, sorter: str('Campaign'), render: (v) => v || '—' },
    { title: 'Request Type', dataIndex: 'RequestType', key: 'RequestType', width: 160, sorter: str('RequestType'), render: (v) => v || '—' },
    { title: 'Session', dataIndex: 'Title', key: 'Title', width: 240, ellipsis: true, sorter: str('Title'),
      render: (v, r) => <Typography.Link onClick={() => navigate(`/coaching/sessions/${r.SessionID}`)}>{v || `#${r.SessionID}`}</Typography.Link> },
    { title: 'Session Status', dataIndex: 'SessionStatus', key: 'SessionStatus', width: 120, sorter: str('SessionStatus'),
      render: (v) => SESSION_LABEL[v] ?? v },
    { title: 'Participant Status', dataIndex: 'ParticipantStatus', key: 'ParticipantStatus', width: 140, sorter: str('ParticipantStatus'),
      render: (v) => <Tag color={PART_COLOR[v] ?? 'default'}>{PART_LABEL[v] ?? v}</Tag> },
    { title: 'Completed At', dataIndex: 'CompletedAt', key: 'CompletedAt', width: 170, sorter: str('CompletedAt'),
      render: (v) => (v ? formatTS(v) : '—') },
    { title: 'Due Date', dataIndex: 'DueDate', key: 'DueDate', width: 110, sorter: str('DueDate'), ...(withAgent ? {} : { defaultSortOrder: 'ascend' }) },
    { title: 'Missed Due Date', dataIndex: 'PastDue', key: 'PastDue', width: 130, align: 'center', sorter: num('PastDue'),
      render: (v) => (v ? <Typography.Text type="danger" strong>1</Typography.Text> : 0) },
  ];

  const panelRows = panel
    ? rows.filter((r) => r.UserID === panel.UserID && (!panel.pastDueOnly || r.PastDue))
    : [];
  const countLink = (value, r, pastDueOnly, danger) => (value
    ? (
      <Button type="link" size="small" style={{ padding: 0 }} danger={danger}
        onClick={() => setPanel({ UserID: r.UserID, AgentName: r.AgentName, pastDueOnly })}>
        {danger ? <strong>{value}</strong> : value}
      </Button>
    )
    : 0);

  if (loading) return <Spin />;
  if (!rows.length) return <Alert type="info" showIcon message="No active or completed coaching sessions are due in this range." />;

  return (
    <div>
      <Space style={{ margin: '12px 0 12px 8px' }} wrap>
        <Segmented value={view} onChange={setView}
          options={[{ label: 'Summary', value: 'summary' }, { label: 'Sessions', value: 'sessions' }]} />
        <Button size="small" icon={<DownloadOutlined />} onClick={exportToExcel}>Export to Excel</Button>
      </Space>

      {view === 'summary' ? (
        <Table
          rowKey="UserID"
          dataSource={byAgent}
          size="small"
          bordered
          pagination={false}
          style={{ maxWidth: 760 }}
          columns={[
            { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', sorter: str('AgentName') },
            { title: 'Sessions', dataIndex: 'Sessions', key: 'Sessions', width: 100, align: 'right', sorter: num('Sessions'),
              render: (v, r) => countLink(v, r, false, false) },
            { title: 'Missed Due Date', dataIndex: 'PastDue', key: 'PastDue', width: 140, align: 'right', sorter: num('PastDue'),
              render: (v, r) => countLink(v, r, true, true) },
            { title: 'On Time', dataIndex: 'OnTime', key: 'OnTime', width: 100, align: 'right', sorter: num('OnTime') },
            { title: 'Not Due Yet', dataIndex: 'Open', key: 'Open', width: 100, align: 'right', sorter: num('Open') },
          ]}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right"><strong>{totals.Sessions}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right"><strong>{totals.PastDue}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right"><strong>{totals.OnTime}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right"><strong>{totals.Open}</strong></Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      ) : (
        <Table
          rowKey={(r) => `${r.SessionID}-${r.UserID}`}
          dataSource={rows}
          size="small"
          bordered
          pagination={{ defaultPageSize: 50, showSizeChanger: true, hideOnSinglePage: true }}
          scroll={{ x: 'max-content' }}
          columns={sessionColumns(true)}
        />
      )}

      <Drawer
        title={panel ? `${panel.pastDueOnly ? 'Missed Due Date' : 'Sessions'}: ${panel.AgentName}` : ''}
        open={!!panel}
        onClose={() => setPanel(null)}
        width={1100}
        destroyOnClose
      >
        <Table
          rowKey={(r) => `${r.SessionID}-${r.UserID}`}
          dataSource={panelRows}
          size="small"
          bordered
          pagination={{ defaultPageSize: 25, hideOnSinglePage: true }}
          scroll={{ x: 'max-content' }}
          columns={sessionColumns(false)}
        />
      </Drawer>
    </div>
  );
}
